import React, { useState, useRef, useEffect } from 'react';
import { X, Check } from 'lucide-react';
import { api } from '../context/GameContext';

/**
 * Drag-based image-crop editor — touch & mouse friendly.
 * Saves percentage focal point (0..100) for both axes that the renderer
 * applies as `object-position: x% y%`.
 *
 * Props:
 *   - mode: 'owner' | 'admin'  — selects backend route
 *   - cardId: string  (used in 'owner' mode)
 *   - templateId: string  (used in 'admin' mode)
 *   - imageUrl: string   (path to the full image)
 *   - initialX, initialY: number (0..100 — current focal point)
 *   - onClose(): void
 *   - onSaved(x, y): void
 */
const CropEditor = ({ mode = 'admin', cardId, templateId, imageUrl, initialX = 50, initialY = 50, onClose, onSaved }) => {
  const [focusX, setFocusX] = useState(Number(initialX));
  const [focusY, setFocusY] = useState(Number(initialY));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const frameRef = useRef(null);
  const dragState = useRef(null); // { startX, startY, baseX, baseY, frameW, frameH }

  // The frame has aspect 3:4 (matches main display). Drag inside it with
  // 1px ≈ (img_size / frame_size) % focus shift. We approximate with
  // (delta / frame * 100) which feels natural in tests.
  const onPointerDown = (e) => {
    e.preventDefault();
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect) return;
    const point = e.touches ? e.touches[0] : e;
    dragState.current = {
      startX: point.clientX,
      startY: point.clientY,
      baseX: focusX,
      baseY: focusY,
      frameW: rect.width,
      frameH: rect.height,
    };
  };

  const onPointerMove = (e) => {
    if (!dragState.current) return;
    e.preventDefault();
    const point = e.touches ? e.touches[0] : e;
    const { startX, startY, baseX, baseY, frameW, frameH } = dragState.current;
    // Drag direction: moving image right means showing more of the LEFT
    // (lower focusX). We invert delta accordingly.
    const dxPct = ((point.clientX - startX) / frameW) * 100;
    const dyPct = ((point.clientY - startY) / frameH) * 100;
    const nextX = Math.max(0, Math.min(100, baseX - dxPct));
    const nextY = Math.max(0, Math.min(100, baseY - dyPct));
    setFocusX(nextX);
    setFocusY(nextY);
  };

  const onPointerUp = () => {
    dragState.current = null;
  };

  // Cleanup global listeners on unmount
  useEffect(() => {
    const move = (e) => onPointerMove(e);
    const up = () => onPointerUp();
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const x = Math.round(focusX * 10) / 10;
      const y = Math.round(focusY * 10) / 10;
      const url = mode === 'owner'
        ? `/cards/${cardId}/focus`
        : `/admin/templates/${templateId}/crop`;
      await api.post(url, { x, y });
      onSaved?.(x, y);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save crop');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid="crop-editor-modal"
    >
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-[#F5F5F7]">Adjust frame</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-[#F5F5F7]"
            data-testid="crop-editor-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* 3:4 preview frame — drag inside to reposition */}
        <div
          ref={frameRef}
          className="relative w-full aspect-[3/4] rounded-lg overflow-hidden bg-black select-none cursor-grab active:cursor-grabbing"
          onMouseDown={onPointerDown}
          onTouchStart={onPointerDown}
          data-testid="crop-editor-frame"
        >
          <img
            src={imageUrl}
            alt="crop preview"
            className="w-full h-full object-cover pointer-events-none"
            style={{ objectPosition: `${focusX}% ${focusY}%` }}
            draggable={false}
          />
          {/* Subtle center-cross guide */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute left-1/2 top-1/2 w-6 h-6 -translate-x-1/2 -translate-y-1/2 border border-white/40 rounded-full" />
          </div>
        </div>

        <p className="text-[10px] text-[#A1A1AA] mt-2 text-center">
          Drag the painting up / down / left / right to choose what's shown.
        </p>

        {/* Sliders for fine-tuning */}
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-[#A1A1AA] w-12">X</span>
            <input
              type="range" min={0} max={100} step={0.5}
              value={focusX}
              onChange={(e) => setFocusX(parseFloat(e.target.value))}
              className="flex-1"
              data-testid="crop-editor-x"
            />
            <span className="text-[10px] font-mono text-[#F5F5F7] w-10 text-right">{focusX.toFixed(0)}%</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-[#A1A1AA] w-12">Y</span>
            <input
              type="range" min={0} max={100} step={0.5}
              value={focusY}
              onChange={(e) => setFocusY(parseFloat(e.target.value))}
              className="flex-1"
              data-testid="crop-editor-y"
            />
            <span className="text-[10px] font-mono text-[#F5F5F7] w-10 text-right">{focusY.toFixed(0)}%</span>
          </div>
        </div>

        {error && (
          <div className="mt-2 text-[11px] text-[#EF4444] text-center">{error}</div>
        )}

        <div className="flex gap-2 mt-4">
          <button
            onClick={() => { setFocusX(50); setFocusY(50); }}
            className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-[#A1A1AA] bg-white/5 border border-white/10"
            data-testid="crop-editor-reset"
          >
            Reset
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex-[2] py-2.5 rounded-xl text-xs font-bold text-white bg-[#5B68F6] disabled:opacity-50 flex items-center justify-center gap-1"
            data-testid="crop-editor-save"
          >
            <Check className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save crop'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CropEditor;
